import ApisServiceApp from './ApisServiceApp'
import { apiRequest } from '@/utils/apiRequestUtils'
import { normalizeListResponse } from '@/utils/apiResponseUtils'
import type {
  ThongTinXuatHoaDonId,
  IThongTinXuatHoaDon,
  IThongTinXuatHoaDonAllQuery,
  IThongTinXuatHoaDonPayload,
  KhachHangId,
} from '@/interfaces/thongTinXuatHoaDonInterfaces'

function prefix(khachHangId: KhachHangId) {
  return `/khach-hang/${khachHangId}/thong-tin-xuat-hoa-don`
}

function toBool(v: unknown): boolean {
  if (typeof v === 'boolean') return v
  if (v === 1 || v === '1' || v === 'true') return true
  return false
}

function str(v: unknown): string {
  return v == null ? '' : String(v)
}

export function mapThongTinXuatHoaDonFromApi(
  raw: Record<string, unknown>,
): IThongTinXuatHoaDon {
  const taiKhoansRaw = Array.isArray(raw.taiKhoans) ? raw.taiKhoans : []
  const taiKhoans = taiKhoansRaw
    .map((row) => {
      const s = row as Record<string, unknown>
      const soTaiKhoan = str(s.soTaiKhoan).trim()
      if (!soTaiKhoan) return null
      return {
        id: s.id != null ? String(s.id) : undefined,
        soTaiKhoan,
        tenNganHang: str(s.tenNganHang),
        tenChuTaiKhoan: str(s.tenChuTaiKhoan),
        macDinh: toBool(s.macDinh),
        thuTu: s.thuTu != null && Number.isFinite(Number(s.thuTu)) ? Number(s.thuTu) : 0,
      }
    })
    .filter((x): x is NonNullable<typeof x> => Boolean(x))

  const legacyTk = str(raw.soTaiKhoanNganHang).trim()
  const resolvedStk =
    taiKhoans.length > 0
      ? taiKhoans
      : legacyTk
        ? [
            {
              soTaiKhoan: legacyTk,
              tenNganHang: str(raw.nganHang),
              macDinh: true,
              thuTu: 0,
            },
          ]
        : []

  return {
    id: String(raw.id ?? ''),
    ten: String(raw.ten ?? ''),
    tenNguoiMua: str(raw.tenNguoiMua),
    maSoThue: str(raw.maSoThue),
    diaChiHoaDon: str(raw.diaChiHoaDon),
    tinhThanhHoaDon: str(raw.tinhThanhHoaDon),
    phuongXaHoaDon: str(raw.phuongXaHoaDon),
    soCCCD: str(raw.soCCCD),
    soHoChieu: str(raw.soHoChieu),
    emailHoaDon: str(raw.emailHoaDon),
    soDienThoaiHoaDon: str(raw.soDienThoaiHoaDon),
    nganHang: str(raw.nganHang),
    soTaiKhoanNganHang: str(raw.soTaiKhoanNganHang),
    taiKhoans: resolvedStk,
    macDinh: toBool(raw.macDinh),
    ngayTao: raw.ngayTao != null ? String(raw.ngayTao) : null,
    ngayCapNhat: raw.ngayCapNhat != null ? String(raw.ngayCapNhat) : null,
  }
}

function toPayload(body: IThongTinXuatHoaDonPayload) {
  const taiKhoans = (body.taiKhoans ?? [])
    .map((stk, idx) => {
      const soTaiKhoan = String(stk.soTaiKhoan ?? '').trim()
      if (!soTaiKhoan) return null
      return {
        ...(stk.id ? { id: stk.id } : {}),
        soTaiKhoan,
        tenNganHang: String(stk.tenNganHang ?? '').trim() || undefined,
        tenChuTaiKhoan: String(stk.tenChuTaiKhoan ?? '').trim() || undefined,
        macDinh: stk.macDinh === true,
        thuTu: typeof stk.thuTu === 'number' ? stk.thuTu : idx,
      }
    })
    .filter((x): x is NonNullable<typeof x> => Boolean(x))

  // Fallback legacy single fields → 1 STK
  if (taiKhoans.length === 0 && body.soTaiKhoanNganHang.trim()) {
    taiKhoans.push({
      soTaiKhoan: body.soTaiKhoanNganHang.trim(),
      tenNganHang: body.nganHang.trim() || undefined,
      tenChuTaiKhoan: undefined,
      macDinh: true,
      thuTu: 0,
    })
  }

  const def = taiKhoans.find((x) => x.macDinh) ?? taiKhoans[0]
  return {
    ten: body.ten.trim(),
    tenNguoiMua: body.tenNguoiMua.trim(),
    maSoThue: body.maSoThue.trim(),
    diaChiHoaDon: body.diaChiHoaDon.trim(),
    tinhThanhHoaDon: body.tinhThanhHoaDon.trim(),
    phuongXaHoaDon: body.phuongXaHoaDon.trim(),
    soCCCD: body.soCCCD.trim(),
    soHoChieu: body.soHoChieu.trim(),
    emailHoaDon: body.emailHoaDon.trim(),
    soDienThoaiHoaDon: body.soDienThoaiHoaDon.trim(),
    nganHang: def?.tenNganHang ?? body.nganHang.trim(),
    soTaiKhoanNganHang: def?.soTaiKhoan ?? body.soTaiKhoanNganHang.trim(),
    taiKhoans,
    macDinh: Boolean(body.macDinh),
  }
}

export function fetchDanhSachThongTinXuatHoaDon(khachHangId: KhachHangId) {
  return apiRequest(
    ApisServiceApp.get(`${prefix(khachHangId)}/danh-sach-thong-tin-xuat-hoa-don`).then((res: unknown) => {
      const { data, paging } = normalizeListResponse(res as Parameters<typeof normalizeListResponse>[0])
      return {
        data: data.map((r) => mapThongTinXuatHoaDonFromApi(r as Record<string, unknown>)),
        paging,
      }
    }),
  )
}

export function fetchDanhSachTatCaThongTinXuatHoaDon(
  query: IThongTinXuatHoaDonAllQuery = {},
) {
  const params: Record<string, string> = {}
  Object.entries(query).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return
    params[k] = String(v)
  })
  return apiRequest(
    ApisServiceApp.get('/thong-tin-xuat-hoa-don/danh-sach-thong-tin-xuat-hoa-don', { params }).then(
      (res: unknown) => {
        const { data, paging } = normalizeListResponse(
          res as Parameters<typeof normalizeListResponse>[0],
        )
        const rows = (data ?? []) as Record<string, unknown>[]
        const mapped = rows.map((r) => {
          const item = mapThongTinXuatHoaDonFromApi(r)
          const kh = r.khachHang as Record<string, unknown> | undefined
          if (kh) {
            item.khachHang = {
              id: String(kh.id ?? ''),
              maKhachHang: String(kh.maKhachHang ?? ''),
              tenKhachHang: String(kh.tenKhachHang ?? ''),
            }
          }
          return item
        })
        return { data: mapped, paging }
      },
    ),
  )
}

export function taoThongTinXuatHoaDon(
  khachHangId: KhachHangId,
  body: IThongTinXuatHoaDonPayload,
) {
  return apiRequest(
    ApisServiceApp.post(`${prefix(khachHangId)}/tao-thong-tin-xuat-hoa-don`, toPayload(body)),
  )
}

export function capNhatThongTinXuatHoaDon(
  khachHangId: KhachHangId,
  thongTinId: ThongTinXuatHoaDonId,
  body: IThongTinXuatHoaDonPayload,
) {
  return apiRequest(
    ApisServiceApp.put(
      `${prefix(khachHangId)}/${thongTinId}/cap-nhat-thong-tin-xuat-hoa-don`,
      toPayload(body),
    ),
  )
}

export function xoaThongTinXuatHoaDon(
  khachHangId: KhachHangId,
  thongTinId: ThongTinXuatHoaDonId,
) {
  return apiRequest(
    ApisServiceApp.delete(`${prefix(khachHangId)}/${thongTinId}/xoa-thong-tin-xuat-hoa-don`),
  )
}
